import {
    collection,
    doc,
    type FirestoreDataConverter,
    getDoc,
    QueryDocumentSnapshot,
    type SnapshotOptions,
    Timestamp,
} from "firebase/firestore";

import { db } from "@/lib/firebase/client.ts";
import type { SiteSettings } from "@/models/site-settings.ts";
import { DatabaseRepository } from "@/repositories/database-repository.ts";

const siteSettingsConverter: FirestoreDataConverter<SiteSettings> = {
    toFirestore(session: SiteSettings) {
        return { ...session };
    },
    fromFirestore(snapshot: QueryDocumentSnapshot, options: SnapshotOptions): SiteSettings {
        const data = snapshot.data(options)!;
        return {
            uid: snapshot.id, // always safe
            ...data,
            createdAt: (data.createdAt as Timestamp)?.toDate(),
            updatedAt: (data.updatedAt as Timestamp)?.toDate(),
        } as SiteSettings;
    },
};

export class SiteSettingsRepository extends DatabaseRepository<SiteSettings> {
    private static instance: SiteSettingsRepository;
    protected readonly collection = collection(db, "siteSettings").withConverter(siteSettingsConverter);

    private constructor() {
        super();
    }

    public static getInstance(): SiteSettingsRepository {
        if (!SiteSettingsRepository.instance) {
            SiteSettingsRepository.instance = new SiteSettingsRepository();
        }
        return SiteSettingsRepository.instance;
    }

    async getById(uid: string): Promise<SiteSettings | null> {
        const ref = doc(this.collection, uid);
        const snap = await getDoc(ref);
        if (!snap.exists()) return null;
        return snap.data() satisfies SiteSettings;
    }

    async getSettings() {
        return this.getById("informant");
    }

    async updateSettings(settings: SiteSettings) {
        return this.updatePartial("informant", settings);
    }
}
